import React, { useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from 'react-router-dom';
import { Col, Divider, message, Row, Spin } from 'antd';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { LoadingOutlined } from '@ant-design/icons';
import { Building } from 'iconsax-react';
import Flag from 'react-world-flags';
import dayjs from 'dayjs';
import TitleText from '../components/atoms/TitleText';
import FormField from '../components/FormField/FormField';
import { AllowedFileTypes } from '../components/FormField/interfaces';
import { getFirstErrorMsg } from '../_utils/formHelpers';

import SectionDesc from '../components/atoms/SectionDesc';
import Label from '../components/atoms/Label';
import InfoText from '../components/atoms/InfoText';
import Button from '../components/atoms/Button';
import DebouncedEmail from '../components/Sellers/AddSeller/DebouncedEmail';
import DebouncedCommercialNumber from '../components/Sellers/AddSeller/DebouncedCommercialNumber';
import DebouncedTaxNumber from '../components/Sellers/AddSeller/DebouncedTaxNumber';
import SignupUsernameField from '../components/Sellers/AddSeller/SignupUsernameField';
import {
  useGetCountriesQuery,
  useGetCountriesDetailsQuery,
  useGetCountryStatesQuery,
  useGetCompanyTypesQuery,
} from '../store/services/lookups';
import { useSharedValidations } from '../hooks/useSharedValidations';
import { useAddSellerMutation } from '../store/services/addSellerService';
import { usePublicState } from '../store/slices/publicSlice';

// ─── Types ────────────────────────────────────────────────────────────────────

const MAX_FILE_SIZE = 5242880;

const ACCEPTED_DOC_TYPES: AllowedFileTypes[] = [
  'image/jpeg',
  'image/png',
  'image/jpg',
  'application/pdf',
];

// ─── Schema ───────────────────────────────────────────────────────────────────

const useAddSellerSchema = () => {
  const {
    zodRequiredStringValidate,
    phoneNumberValidation,
    stateValidation,
    streetValidation,
    UploadFileSchema,
    namesValidation,
  } = useSharedValidations();

  const { t } = useTranslation();
  const requiredError = t('components.validations.requiredField');

  const refinedUploadFileSchema = UploadFileSchema.refine(
    ({ type }) => {
      if (!!type) {
        return ACCEPTED_DOC_TYPES.includes((type as AllowedFileTypes) ?? '');
      } else {
        return true;
      }
    },
    { message: t('components.validations.invalidDocFileType') }
  ).refine((file) => (file.size ?? 0) <= MAX_FILE_SIZE, {
    message: t('components.validations.maxFileSize', { size: 5 }),
  });

  const baseSchema = z.object({
    email: zodRequiredStringValidate.email(
      t('components.validations.invalidEmail')
    ),
    phoneNumber: phoneNumberValidation,
    birthDate: z.custom<dayjs.Dayjs>(
      (val) => val instanceof Object && 'isValid' in val && val.isValid(),
      { message: requiredError }
    ).refine((date) => dayjs().diff(date, 'years') >= 18, {
      message: t('components.validations.ageRestriction', { age: 18 }),
    }),
    username: zodRequiredStringValidate
      .min(3, t('components.validations.minLength', { value: 3 }))
      .max(10, t('components.validations.maxLength', { value: 10 }))
      .regex(/^(?!\d+$)[a-zA-Z0-9]+$/, {
        message: t('components.validations.alphanumeric'),
      }),
    name: zodRequiredStringValidate
      .regex(/^.{1,}$/, { message: requiredError })
      .trim()
      .min(2, t('components.validations.minLength', { value: 2 }))
      .max(20, t('components.validations.maxLength', { value: 20 })),
    companyType: zodRequiredStringValidate,
    countryId: z.union([z.string(), z.number()], {
      required_error: requiredError,
    }),
    state: stateValidation,
    street: streetValidation,
    commercialRegister: zodRequiredStringValidate
      .min(4, t('components.validations.commercialRegisterLength'))
      .max(15, t('components.validations.commercialRegisterLength'))
      .regex(/^[0-9]+$/, t('components.validations.acceptNumbersOnly'))
      .regex(/^\d{4,}$/, t('components.validations.commercialRegisterLength')),
    taxNumber: zodRequiredStringValidate
      .min(4, t('components.validations.taxLength'))
      .max(15, t('components.validations.taxLength'))
      .regex(/^[0-9]+$/, t('components.validations.acceptNumbersOnly'))
      .regex(/^\d{4,}$/, t('components.validations.taxLength')),
    commercialRegisterDocs: z
      .array(refinedUploadFileSchema)
      .min(1, { message: requiredError })
      .max(3),
    taxNumberDocuments: z
      .array(refinedUploadFileSchema)
      .min(1, { message: requiredError })
      .max(3),
  });

  const schema = z.intersection(namesValidation, baseSchema);

  return { schema };
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const disableDatesUnder18 = (date: dayjs.Dayjs): boolean => {
  return date.isAfter(dayjs().subtract(18, 'years'));
};



// ─── Component ────────────────────────────────────────────────────────────────

const AddSeller = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { schema } = useAddSellerSchema();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isSidebarCollapsed } = usePublicState();
  const spanValue = isSidebarCollapsed ? 20 : 24;

  type FormFieldType = z.infer<typeof schema>;

  const methods = useForm<FormFieldType>({
    resolver: zodResolver(schema),
    mode: 'all',
    defaultValues: {
      phoneNumber: {
        code: '966',
        countryCode: 'SA',
      },
      commercialRegisterDocs: [],
      taxNumberDocuments: [],
    },
  });

  const country = methods.watch('countryId');
  const commercialRegisterDocsLength = (methods.watch('commercialRegisterDocs') ?? []).length;
  const taxNumberDocumentsLength = (methods.watch('taxNumberDocuments') ?? []).length;

  // ─── RTK Queries ──────────────────────────────────────────────────────────

  const { data: countries } = useGetCountriesQuery();
  const { data: countriesInDetails } = useGetCountriesDetailsQuery();
  const { data: companyTypes } = useGetCompanyTypesQuery();
  const { data: states, isFetching: statesLoading } = useGetCountryStatesQuery(
    { countryId: country?.toString() },
    { skip: !country }
  );

  const [addSeller] = useAddSellerMutation();

  // ─── Submit Handler ───────────────────────────────────────────────────────

  const onSubmit = async (data: FormFieldType) => {
    const formData = new FormData();

    formData.append('firstName', data.firstName || '');
    formData.append('lastName', data.lastName || '');
    formData.append('companyName', data.name || '');
    formData.append('email', data.email || '');
    formData.append('birthDate', data.birthDate?.format('YYYY-MM-DD') || '');
    formData.append('companyTypeId', data.companyType || '');
    formData.append('commercialRegister', data.commercialRegister || '');
    formData.append('taxNumber', data.taxNumber || '');
    formData.append('userName', data.username || '');
    if (data.street) formData.append('address.street', data.street);
    formData.append('address.stateId', data.state || '');
    formData.append('address.countryId', data.countryId?.toString() || '');

    const hasPlus = data.phoneNumber?.code?.includes('+');
    formData.append(
      'phone.phoneCode',
      `${hasPlus ? '' : '+'}${data.phoneNumber?.code}`
    );
    formData.append('phone.phoneNumber', data.phoneNumber?.number);

    // Docs Destruct
    for (const index in data.commercialRegisterDocs) {
      if (data.commercialRegisterDocs[index]?.originFileObj) {
        formData.append(
          `commercialRegisterDocuments`,
          data.commercialRegisterDocs[index].originFileObj
        );
      }
    }
    for (const index in data.taxNumberDocuments) {
      if (data.taxNumberDocuments[index]?.originFileObj) {
        formData.append(
          `taxNumberDocuments`,
          data.taxNumberDocuments[index].originFileObj
        );
      }
    }

    try {
      setIsSubmitting(true);

      const response = await addSeller(formData).unwrap();
      message.success(t('sellers.addSeller.submitSuccess'));
      methods.reset();

      const sellerId = response?.payload?.profileId;
      navigate(`/sellers/${sellerId}/profile-creation`);

    } catch (err: unknown) {
      const error = err as {
        data?: { message?: string; errors?: Record<string, string> };
      };
      if (error?.data?.errors) {
        Object.entries(error.data.errors).forEach(([field, msg]) => {
          // Map backend field names to client names if necessary
          const fieldMap: Record<string, string> = {
            commercialRegisterDocuments: 'commercialRegisterDocs',
          };
          const rhfField = fieldMap[field] || field;
          // @ts-expect-error dynamic field name
          methods.setError(rhfField, { type: 'server', message: msg });
        });
        const firstError = Object.values(error.data.errors)[0];
        if (firstError) message.error(firstError);
      } else {
        message.error(
          error?.data?.message || t('sellers.addSeller.submitError')
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const onError = (errors: any) => {
    console.log('FORM VALIDATION ERRORS:', errors);
    const firstErrorField = Object.keys(errors)[0];
    const el = document.getElementById(firstErrorField) || document.querySelector(`[name="${firstErrorField}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (typeof (el as HTMLElement).focus === 'function') {
        (el as HTMLElement).focus();
      }
    }
  };

  return (
    <Row justify="center" data-testid="add-seller-page" className="w-full">
      <Col span={24} className="space-y-6">

        {/* ─── Page Header ──────────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between mb-2">
          <div>
            <TitleText
              size="2xl"
              className="font-bold text-gray-900 dark:text-white m-0 mb-1"
              text={t('sellers.addSeller.pageTitle')}
            />
            <div className="text-sm text-gray-500 flex items-center gap-1">
              <Link to="/" className="hover:text-gray-900 dark:hover:text-gray-200 transition-colors">
                <InfoText text={t('sellers.addSeller.breadcrumbs.dashboard')} />
              </Link>
              <InfoText text="/" />
              <Link to="/sellers" className="hover:text-gray-900 dark:hover:text-gray-200 transition-colors">
                <InfoText text={t('sellers.addSeller.breadcrumbs.sellersList')} />
              </Link>
              <InfoText text="/" />
              <InfoText className="text-gray-900 font-medium dark:text-gray-300" text={t('sellers.addSeller.breadcrumbs.addUser')} />
            </div>
          </div>
          <div>
            <Button
              id="register-submit-top"
              type="submit"
              form="add-seller-form"
              variant="primary"
              loading={isSubmitting}
              className="rounded-[12px] text-sm px-6 py-3 flex items-center justify-center gap-2 font-medium transition-all"
              data-testid="business-form-submit-button-top"
            >
              {t('components.saveAndContinue')}
            </Button>
          </div>
        </div>

        {/* ─── Form Card ────────────────────────────────────────────────────────── */}
        <div
          className="bg-white dark:bg-dark-surface-primary rounded-[8px] border border-gray-200 dark:border-dark-input-border"
          data-testid="add-seller-form-card"
        >
          {/* Logo + Title */}
          <div className="p-6 flex flex-col gap-2" data-testid="add-seller-form-top">
            <div
              className="w-[48px] h-[48px] rounded-full p-1 bg-gradient-to-b from-[#C1C1C1] to-[#F5F5F5] dark:from-gray-700 dark:to-gray-800 border border-gray-200 dark:border-dark-input-border flex items-center justify-center mb-2"
              data-testid="add-seller-logo"
            >
              <div className="w-full h-full rounded-full bg-white dark:bg-dark-surface-secondary flex items-center justify-center">
                <Building size="20" className="text-[#111928] dark:text-dark-text-primary" variant="Bold" />
              </div>
            </div>
            <TitleText
              text={t('sellers.addSeller.cardTitle')}
              className="text-[24px] font-bold text-[#090909] dark:text-dark-text-primary leading-none"
            />
            <InfoText
              text={t('sellers.addSeller.pageSubtitle')}
              className="text-[14px] text-[#777777] dark:text-dark-text-secondary font-normal w-full block"
            />
          </div>
          <Divider className="m-0 border-gray-200 dark:border-dark-input-border" />

          <FormProvider {...methods}>
            <form
              id="add-seller-form"
              className="[&_label]:!text-[#090909] dark:[&_label]:!text-dark-text-primary"
              onSubmit={methods.handleSubmit(onSubmit, onError)}
              data-testid="add-seller-form"
            >
              {/* ─── User Information Section ──────────────────────────────────── */}
              <div className="p-6" data-testid="add-seller-user-info-section">
                <div className="mb-5">
                  <h3 className="text-[#141414] dark:text-dark-text-primary text-xl font-semibold max-w-full break-words whitespace-pre-wrap">
                    {t('sellers.addSeller.userInformation')}
                  </h3>
                  <SectionDesc text={t('sellers.addSeller.userInformationSubtitle')} />
                </div>

                <Row gutter={[20, 20]}>
                  <Col span={24} sm={12}>
                    <FormField
                      id="firstName"
                      name="firstName"
                      type="text"
                      label={t('sellers.addSeller.firstName')}
                      placeholder={t('sellers.addSeller.firstNamePlaceholder')}
                      data-testid="add-seller-firstname"
                      onChange={(e) => {
                        // @ts-expect-error event typing
                        const val = e?.target?.value as string;
                        methods.setValue('firstName', val?.replace(/\s+/g, ''));
                      }}
                    />
                  </Col>

                  <Col span={24} sm={12}>
                    <FormField
                      id="lastName"
                      name="lastName"
                      type="text"
                      label={t('sellers.addSeller.lastName')}
                      placeholder={t('sellers.addSeller.lastNamePlaceholder')}
                      data-testid="add-seller-lastname"
                      onChange={(e) => {
                        // @ts-expect-error event typing
                        const val = e?.target?.value as string;
                        methods.setValue('lastName', val?.replace(/\s+/g, ''));
                      }}
                    />
                  </Col>

                  <Col span={24}>
                    <DebouncedEmail dataTestId="add-seller-email" />
                  </Col>

                  <Col span={24} sm={12}>
                    <FormField
                      id="phoneNumber"
                      name="phoneNumber"
                      type="mobile"
                      label={t('sellers.addSeller.phoneNumber')}
                      data-testid="add-seller-phone"
                    />
                  </Col>

                  <Col span={24} sm={12}>
                    <FormField
                      id="birthDate"
                      name="birthDate"
                      type="date"
                      label={t('sellers.addSeller.dateOfBirth')}
                      placeholder={t('sellers.addSeller.dateOfBirthPlaceholder')}
                      data-testid="add-seller-birth-date"
                      disabledDateCustom={disableDatesUnder18}
                      maxDate={dayjs().subtract(18, 'years')}
                      minDate={dayjs().subtract(100, 'years')}
                    />
                  </Col>

                  <Col span={24}>
                    <SignupUsernameField dataTestId="add-seller-username" />
                  </Col>

                  <Col span={24} sm={12}>
                    <FormField
                      id="name"
                      name="name"
                      type="text"
                      label={t('sellers.addSeller.companyName')}
                      placeholder={t('sellers.addSeller.companyNamePlaceholder')}
                      data-testid="add-seller-company-name"
                    />
                  </Col>

                  <Col span={24} sm={12}>
                    <FormField
                      id="companyType"
                      name="companyType"
                      type="select"
                      label={t('sellers.addSeller.companyType')}
                      placeholder={t('sellers.addSeller.companyTypePlaceholder')}
                      data-testid="add-seller-company-type"
                      options={companyTypes?.map((c) => ({
                        label: c.name,
                        value: String(c.id),
                      }))}
                    />
                  </Col>
                </Row>
              </div>

              {/* ─── Address Section ─────────────────────────────────────────────── */}
              <Divider className="m-0 border-gray-200 dark:border-dark-input-border" />
              <div className="p-6" data-testid="add-seller-address-section">
                <div className="mb-5">
                  <h3 className="text-[#141414] dark:text-dark-text-primary text-xl font-semibold max-w-full break-words whitespace-pre-wrap">
                    {t('sellers.addSeller.addressInformation')}
                  </h3>
                  <SectionDesc text={t('sellers.addSeller.addressInformationSubtitle')} />
                </div>

                <Row gutter={[20, 20]}>
                  <Col span={24} sm={12}>
                    <FormField
                      id="countryId"
                      name="countryId"
                      type="select"
                      showSearch
                      label={t('sellers.addSeller.country')}
                      placeholder={t('sellers.addSeller.countryPlaceholder')}
                      data-testid="add-seller-country"
                      options={countries?.map((item) => ({
                        label: item?.name,
                        value: item?.id,
                      }))}
                      onChange={(v) => {
                        methods.setValue('countryId', v as string);
                        methods.resetField('state');
                      }}
                    />
                  </Col>

                  <Col span={24} sm={12}>
                    <FormField
                      id="state"
                      name="state"
                      type="select"
                      label={t('sellers.addSeller.state')}
                      placeholder={t('sellers.addSeller.statePlaceholder')}
                      data-testid="add-seller-state"
                      disabled={statesLoading || !country}
                      options={states?.map((s) => ({
                        label: s.name,
                        value: String(s.id),
                      }))}
                    />
                  </Col>

                  <Col span={24}>
                    <FormField
                      id="street"
                      name="street"
                      type="text"
                      label={
                        <span>
                          {t('sellers.addSeller.streetAddress')}{' '}
                          <span className="text-gray-400 dark:text-dark-text-secondary text-sm font-normal">
                            {t('components.optional') || '(Optional)'}
                          </span>
                        </span>
                      }
                      placeholder={t('sellers.addSeller.streetAddressPlaceholder')}
                      data-testid="add-seller-street"
                    />
                  </Col>
                </Row>
              </div>

              {/* ─── Company Papers Section ────────────────────────────────────── */}
              <Divider className="m-0 border-gray-200 dark:border-dark-input-border" />
              <div className="px-6 pb-8 pt-6" data-testid="add-seller-company-papers-section">
                <div className="mb-5">
                  <h3 className="text-[#141414] dark:text-dark-text-primary text-xl font-semibold max-w-full break-words whitespace-pre-wrap">
                    {t('sellers.addSeller.companyPapers')}
                  </h3>
                  <SectionDesc text={t('sellers.addSeller.companyPapersSubtitle')} />
                </div>

                <Row gutter={[20, 20]}>
                  <Col span={24} sm={12}>
                    <DebouncedCommercialNumber dataTestId="add-seller-commercial-number" />
                  </Col>

                  <Col span={24} sm={12}>
                    <DebouncedTaxNumber dataTestId="add-seller-tax-number" />
                  </Col>

                  <Col span={24}>
                    <FormField
                      maxCount={3}
                      type="file"
                      allowedTypes={ACCEPTED_DOC_TYPES}
                      disabled={commercialRegisterDocsLength >= 3}
                      label={t('sellers.addSeller.commercialRegisterDocs')}
                      id="commercialRegisterDocs"
                      name="commercialRegisterDocs"
                      uploadHints={[
                        `${t('sellers.addSeller.acceptedFilesType')}: [.jpg, .png, .pdf]`,
                        t('sellers.addSeller.maxFiles', { filesNum: 3, fileSize: 5 }),
                      ]}
                      data-testid="add-seller-commercial-docs"
                    />
                  </Col>

                  <Col span={24}>
                    <FormField
                      maxCount={3}
                      type="file"
                      allowedTypes={ACCEPTED_DOC_TYPES}
                      disabled={taxNumberDocumentsLength >= 3}
                      maxFileSize={5}
                      label={t('sellers.addSeller.taxNumberDocs')}
                      id="taxNumberDocuments"
                      name="taxNumberDocuments"
                      uploadHints={[
                        `${t('sellers.addSeller.acceptedFilesType')}: [.jpg, .png, .pdf]`,
                        t('sellers.addSeller.maxFiles', { filesNum: 3, fileSize: 5 }),
                      ]}
                      data-testid="add-seller-tax-docs"
                    />
                  </Col>

                </Row>
              </div>

            </form>
          </FormProvider>
        </div>
      </Col>
    </Row>
  );
};

export default AddSeller;
